import { UrlKeyDto } from '@trucktrax/trucktrax-ts-common';

import {
  EMAIL_NOTIFICATION_TYPE_LABELS,
  EmailNotificationSubscriptionDto,
  EmailNotificationType,
  EmailSubscriptionGroup,
} from '../types';

/**
 * Shared behaviour for the Email Notifications admin screen.
 *
 * The API stores flat rows -- one per (email, notification type, plant) -- while an
 * admin configures an address. Everything that bridges those two shapes lives here:
 * grouping rows into addresses for the table, expanding an address's choices back
 * into rows on save, and diffing that against what already exists.
 *
 * Kept free of service and component imports so it can be exercised on its own.
 */

/** Every type, in the order the Notifications dropdown offers them. */
export const NOTIFICATION_TYPE_OPTIONS = [
  EmailNotificationType.IncomingMaterials,
  EmailNotificationType.Diversions,
].map(type => ({ key: type, label: EMAIL_NOTIFICATION_TYPE_LABELS[type] }));

/** Addresses are compared case- and whitespace-insensitively; the API is not. */
export const normalizeEmail = (email?: string | null) => (email ?? '').trim().toLowerCase();

/* ---------------------------------------------------------------------------
 * Grouping: flat rows in, one row per address out.
 * ------------------------------------------------------------------------- */

const byName = (a: { name?: string }, b: { name?: string }) =>
  (a.name ?? '').localeCompare(b.name ?? '');

/** Distinct UrlKeyDtos by url, name-sorted, so a cell reads the same on every render. */
function distinctByUrl(values: (UrlKeyDto | null | undefined)[]): UrlKeyDto[] {
  const byUrl = new Map<string, UrlKeyDto>();
  values.forEach(value => {
    if (value?.url && !byUrl.has(value.url)) {
      byUrl.set(value.url, value);
    }
  });
  return Array.from(byUrl.values()).sort(byName);
}

/**
 * One row per address, carrying the union of everything that address is subscribed
 * to.
 *
 * Grouping is on the lowercased address, so rows written with different casing --
 * which the backend permits, its uniqueness check comparing `s.Email == dto.Email`
 * under the database collation -- collapse into the one row an admin expects rather
 * than appearing as two identical-looking entries.
 *
 * The rollup status is deliberately not stored on the group: see {@link groupStatus},
 * which reads it from the rows. A boolean here would have to choose between calling an
 * eleven-of-twelve address active or inactive, and both misreport it.
 *
 * Groups come back address-sorted; the table sorts on top of that, and a stable
 * base order keeps rows from reshuffling between reads.
 */
export function groupSubscriptions(
  subscriptions: EmailNotificationSubscriptionDto[],
): EmailSubscriptionGroup[] {
  const groups = new Map<string, EmailSubscriptionGroup>();

  subscriptions.forEach(subscription => {
    const key = normalizeEmail(subscription.email);
    if (key === '') {
      return;
    }

    const existing = groups.get(key);
    if (existing) {
      existing.subscriptions.push(subscription);
      return;
    }

    groups.set(key, {
      key,
      email: subscription.email,
      notificationTypes: [],
      regions: [],
      plants: [],
      subscriptions: [subscription],
    });
  });

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      // Ordered by the dropdown's order rather than by first appearance, so two
      // addresses with the same topics render them identically.
      notificationTypes: NOTIFICATION_TYPE_OPTIONS.map(option => option.key).filter(type =>
        group.subscriptions.some(subscription => subscription.notificationType === type),
      ),
      regions: distinctByUrl(group.subscriptions.map(subscription => subscription.region)),
      plants: distinctByUrl(group.subscriptions.map(subscription => subscription.plant)),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

/* ---------------------------------------------------------------------------
 * Expansion: an address's choices out, flat rows in.
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * Notification cells
 *
 * The modal offers a checkbox per (topic, plant): a tab per notification topic, and
 * under it the plants grouped by region. A ticked cell IS a stored row -- the API keys
 * a subscription on (email, type, region, plant), and a cell names the type and the
 * plant, with the region travelling along because a plant belongs to one.
 *
 * That correspondence is the point. The two dropdowns this replaced could only ever
 * express a full cross-product of the topics chosen against the plants chosen, so
 * "Incoming Materials at Ash Grove, Diversions at Bellingham" was not sayable -- the
 * stored rows could hold it, the form could not.
 * ------------------------------------------------------------------------- */

/** One checkbox in the field: a topic against a plant, carrying that plant's region. */
export interface NotificationCell {
  notificationType: EmailNotificationType;
  plantUrl: string;
  regionUrl: string;
}

/**
 * A cell's identity, and what the modal's field value is a list of.
 *
 * Region is deliberately not in the key: a plant belongs to exactly one region, so
 * the plant url already determines it, and including it would let the same cell be
 * keyed two ways.
 */
export const cellKey = (notificationType: EmailNotificationType, plantUrl: string) =>
  `${notificationType}|${plantUrl}`;

/** The cell a stored row corresponds to, or undefined for a row the field cannot show. */
export const cellKeyOf = (
  subscription: Pick<EmailNotificationSubscriptionDto, 'notificationType' | 'plant'>,
): string | undefined =>
  subscription.plant?.url
    ? cellKey(subscription.notificationType, subscription.plant.url)
    : undefined;

/**
 * The cells to tick for an address, deduped.
 *
 * Rows with no plant have no cell -- see `makePlantLabel` for where those come from.
 * They are skipped here and, importantly, also withheld from the save diff, or a save
 * would read them as rows the admin had unticked and delete them.
 */
export const subscriptionCellKeys = (subscriptions: EmailNotificationSubscriptionDto[]): string[] =>
  Array.from(new Set(subscriptions.map(cellKeyOf).filter((key): key is string => !!key)));

/** A subscription as it goes to the API: no id, no url, and `active` set by the service. */
export type NewSubscription = Omit<EmailNotificationSubscriptionDto, 'id' | 'url' | 'active'>;

/**
 * The rows a set of ticked cells asks for -- one row per cell, which is why the form
 * needs no expansion step.
 *
 * A key naming a plant the field does not know about is dropped rather than written as
 * a row with no region: ValidateDto requires the plant to be in the row's region, and
 * only the plant list knows which region that is.
 */
export function cellsToSubscriptions(
  email: string,
  selectedKeys: string[],
  cells: NotificationCell[],
): NewSubscription[] {
  const trimmed = email.trim();
  const byKey = new Map(cells.map(cell => [cellKey(cell.notificationType, cell.plantUrl), cell]));

  return selectedKeys
    .map(key => byKey.get(key))
    .filter((cell): cell is NotificationCell => !!cell)
    .map(cell => ({
      email: trimmed,
      notificationType: cell.notificationType,
      region: { url: cell.regionUrl },
      plant: { url: cell.plantUrl },
    }));
}

/**
 * Identifies a row by everything the backend's uniqueness check keys on:
 * email, type, region and plant. Two rows with the same signature are the same
 * subscription, whatever their ids.
 */
export function subscriptionSignature(
  subscription: Pick<
    EmailNotificationSubscriptionDto,
    'email' | 'notificationType' | 'region' | 'plant'
  >,
): string {
  return [
    normalizeEmail(subscription.email),
    subscription.notificationType,
    subscription.region?.url ?? '',
    subscription.plant?.url ?? '',
  ].join('|');
}

export interface SubscriptionDiff {
  /** Rows to POST. */
  toCreate: NewSubscription[];
  /** Existing rows to DELETE. */
  toDelete: EmailNotificationSubscriptionDto[];
  /** Existing rows that survive untouched, so a save that changes nothing writes nothing. */
  unchanged: EmailNotificationSubscriptionDto[];
}

/**
 * What has to be written to make `existing` match `desired`.
 *
 * Rows are never updated in place: a subscription is entirely made of the fields
 * its uniqueness is keyed on, so "changing" one is indistinguishable from deleting
 * it and creating another. Only the status toggle updates a row, and that changes
 * `active`, which is not part of the signature.
 *
 * Duplicate signatures within `existing` -- possible for rows that differ only by
 * email casing -- collapse onto the first, and the rest are deleted, which is what
 * clears them out the first time such an address is saved.
 */
export function diffSubscriptions(
  desired: NewSubscription[],
  existing: EmailNotificationSubscriptionDto[],
): SubscriptionDiff {
  const keep = new Map<string, EmailNotificationSubscriptionDto>();
  const toDelete: EmailNotificationSubscriptionDto[] = [];
  const wanted = new Set(desired.map(subscriptionSignature));

  existing.forEach(subscription => {
    const signature = subscriptionSignature(subscription);
    if (wanted.has(signature) && !keep.has(signature)) {
      keep.set(signature, subscription);
      return;
    }
    toDelete.push(subscription);
  });

  return {
    toCreate: desired.filter(subscription => !keep.has(subscriptionSignature(subscription))),
    toDelete,
    unchanged: Array.from(keep.values()),
  };
}

/* ---------------------------------------------------------------------------
 * Display
 * ------------------------------------------------------------------------- */

/** The notification type, as an admin reads it. */
export const subscriptionTypeLabel = (subscription: EmailNotificationSubscriptionDto) =>
  EMAIL_NOTIFICATION_TYPE_LABELS[subscription.notificationType];

/**
 * Names one row's plant, given the name map built from the plant-option read.
 *
 * The map is not optional decoration: the API returns a row's plant as a bare url
 * (`ToDto` maps `Plant = subscription.PlantUrl`), so there is no name on the row to
 * fall back to. An earlier version of this reached for `plant.name` and rendered an
 * empty cell for every row.
 *
 * A plant the map no longer carries -- an archived one, say -- falls back to its url
 * rather than leaving a gap.
 */
export const makePlantLabel =
  (plantNames: { [url: string]: string }) =>
  (subscription: EmailNotificationSubscriptionDto): string => {
    const url = subscription.plant?.url;
    if (!url) {
      // MatchesPlant reads a row with no plant as covering the whole region. The
      // write path no longer creates them, but rows from before DEV-4412 exist.
      return 'All plants in region';
    }
    return plantNames[url] ?? url;
  };

/**
 * An address's rollup status.
 *
 * Three states rather than two because status is toggled per row: an address with
 * eleven of twelve rows on is neither Active nor Inactive, and calling it either
 * misreports what is actually sending.
 */
export type GroupStatus = 'active' | 'partial' | 'inactive';

export const GROUP_STATUS_LABELS: Record<GroupStatus, string> = {
  active: 'Active',
  partial: 'Partial',
  inactive: 'Inactive',
};

export function groupStatus(group: EmailSubscriptionGroup): GroupStatus {
  if (group.subscriptions.every(subscription => subscription.active)) {
    return 'active';
  }
  return group.subscriptions.some(subscription => subscription.active) ? 'partial' : 'inactive';
}

/**
 * Rows in a stable reading order: by notification type as the dropdown orders them,
 * then region, then plant.
 *
 * Takes the rows rather than a group, because both places that list them -- the table's
 * expanded detail and the user profile's subscriptions card -- have to agree, and only
 * one of them has a group.
 */
export function sortedSubscriptions(
  subscriptions: EmailNotificationSubscriptionDto[],
  regionNames: { [url: string]: string } = {},
  plantNames: (subscription: EmailNotificationSubscriptionDto) => string = () => '',
): EmailNotificationSubscriptionDto[] {
  const typeOrder = NOTIFICATION_TYPE_OPTIONS.map(option => option.key);

  return [...subscriptions].sort((a, b) => {
    const byType = typeOrder.indexOf(a.notificationType) - typeOrder.indexOf(b.notificationType);
    if (byType !== 0) {
      return byType;
    }
    const byRegion = (regionNames[a.region?.url ?? ''] ?? '').localeCompare(
      regionNames[b.region?.url ?? ''] ?? '',
    );
    return byRegion !== 0 ? byRegion : plantNames(a).localeCompare(plantNames(b));
  });
}

/** The Notifications cell: the group's topics, as an admin reads them. */
export const groupTypeLabels = (group: EmailSubscriptionGroup) =>
  group.notificationTypes.map(type => EMAIL_NOTIFICATION_TYPE_LABELS[type]);
